import { useMemo, useState } from "react"

import { FileText } from "lucide-react"

import { ReportAdditionalDocumentSelection } from "@/features/report/ReportAdditionalDocumentSelection"
import { SelectedDocumentPanel } from "@/features/report/SelectedDocumentPanel"
import { Badge } from "@ui/badge"

import { ContentObjectItem } from "@vertesia/common"

const US_LOCAL = "en-US"

interface AdditionalDocumentsSelectionPanelProps {
    documents: ContentObjectItem[]
    handleDocumentSelection: (documents: ContentObjectItem[]) => void
    handleDocumentsRemoved: (documents: ContentObjectItem[]) => void
}
export function AdditionalDocumentsSelectionPanel({ documents, handleDocumentSelection, handleDocumentsRemoved }: Readonly<AdditionalDocumentsSelectionPanelProps>) {
    const [typeFilter, setTypeFilter] = useState<string | undefined>(undefined)

    const handleRemoveFromSelection = (doc: ContentObjectItem) => {
        handleDocumentsRemoved(documents.filter(d => d.id !== doc.id))
    }

    const handleTypeFilterSelection = (typeId: string) => {
        setTypeFilter(typeFilter === typeId ? undefined : typeId)
    }

    const filteredDocuments = useMemo(() => {
        if (!typeFilter) {
            return documents
        }
        return documents.filter(doc => doc.type?.id === typeFilter)
    }, [documents, typeFilter])

    const documentsTypes = useMemo(() => {
        if (!documents?.length) {
            return []
        }

        const types: any[] = documents.map((doc) => doc.type)
        return Object.values(
            types.reduce((acc, item) => {
                if (acc[item.id]) {
                    acc[item.id].count++
                } else {
                    acc[item.id] = { ...item, count: 1 }
                }
                return acc
            }, {})
        ).sort((a: any, b: any) => a.name.localeCompare(b.name))
    }, [documents])

    return (
        <>
            {
                (documents.length === 0) ? (
                    <div className="flex flex-col items-center justify-center h-full gap-4">
                        <FileText className="h-16 w-16 text-gray-300" />
                        <div className="text-center">
                            <h3 className="text-lg font-medium text-gray-900">
                                No additional documents selected
                            </h3>
                            <p className="text-gray-600">
                                Search to find and select additional documents for your report
                            </p>
                        </div>
                        <ReportAdditionalDocumentSelection handleDocumentSelection={handleDocumentSelection} />
                    </div>
                ) : (
                    <div className="flex flex-col h-full min-h-0 gap-2">
                        <div className="flex flex-wrap gap-2">
                            {
                                documentsTypes.map((type: any) => (
                                    <Badge key={type.id} onClick={() => handleTypeFilterSelection(type.id)} className={`hover:cursor-pointer ${typeFilter === type.id && "bg-blue-500 text-white hover:bg-blue-500/80"}`}>
                                        {type.name} ({type.count})
                                    </Badge>
                                ))
                            }
                        </div>
                        <div className="flex-1 min-h-0 overflow-y-auto">
                            <ul className="space-y-2">
                                {
                                    filteredDocuments.map((doc) => (
                                        <SelectedDocumentPanel document={doc} onRemoveFromSelection={handleRemoveFromSelection} />
                                    ))
                                }
                            </ul>
                        </div>
                    </div>
                )
            }
        </>
    )
}
